/**
 * Erreurs client (super admin) — W7, SU-5, CEO-14, ENG-17.
 *
 * The errors the browsers reported (lib/clientErrorReporter.ts → POST
 * /api/client-errors), kept 14 days, emails, phone numbers and tokens
 * removed by the server before storage. A server-paged DataTable (cursor
 * pages with the total) with filters in the address: type, start of the
 * page path, text of the message, period, and « même erreur » (the
 * fingerprint). The stack is folded under each row.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable, type DataTableColumn, type FilterPill } from '@/components/shared/DataTable';
import { PlatformPageHeader } from '@/components/backoffice/platform/PlatformBits';
import { useCursorPages } from '@/hooks/useCursorPages';
import { listClientErrors, CLIENT_ERROR_KINDS, type ClientErrorFilters, type ClientErrorItem, type ClientErrorKind } from '@/api/endpoints/clientErrors';
import { toUserMessage } from '@/lib/errorToast';
import { ApiErrorException } from '@/api/client';
import { currentLocale } from '@/i18n/locale';

const PAGE_SIZE = 50;
const ALL = '__all__';
const PERIODS = ['24h', '7d', '14d'] as const;
type Period = (typeof PERIODS)[number];
const PERIOD_MS: Record<Period, number> = { '24h': 86_400_000, '7d': 7 * 86_400_000, '14d': 14 * 86_400_000 };

export default function AdminClientErrors() {
  const { t } = useTranslation(['admin', 'common']);
  const [params, setParams] = useSearchParams();
  const kindParam = params.get('kind') ?? '';
  const kind = (CLIENT_ERROR_KINDS as string[]).includes(kindParam) ? (kindParam as ClientErrorKind) : '';
  const period = (PERIODS as readonly string[]).includes(params.get('period') ?? '') ? (params.get('period') as Period) : '7d';
  const route = params.get('route') ?? '';
  const q = params.get('q') ?? '';
  const fingerprint = params.get('fingerprint') ?? '';
  const [routeDraft, setRouteDraft] = useState(route);
  const [qDraft, setQDraft] = useState(q);
  const [now, setNow] = useState(() => Date.now());

  const latestParams = useRef(params);
  latestParams.current = params;
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(latestParams.current);
    if (value) next.set(key, value); else next.delete(key);
    latestParams.current = next;
    setParams(next, { replace: true });
  };

  // Typing filters after a short pause.
  useEffect(() => {
    if (qDraft.trim() === q) return;
    const id = setTimeout(() => setParam('q', qDraft.trim()), 350);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDraft]);
  useEffect(() => {
    if (routeDraft.trim() === route) return;
    const id = setTimeout(() => setParam('route', routeDraft.trim()), 350);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeDraft]);

  const filters: ClientErrorFilters = useMemo(() => ({
    kind: kind || undefined,
    route: route || undefined,
    q: q || undefined,
    fingerprint: fingerprint || undefined,
    from: new Date(now - PERIOD_MS[period]).toISOString(),
  }), [kind, route, q, fingerprint, period, now]);

  const { query, page, onPageChange } = useCursorPages(
    ['admin', 'client-errors', filters],
    (cursor, signal) => listClientErrors(filters, { limit: PAGE_SIZE, cursor }, signal),
  );

  const fmt = (iso: string) => new Date(iso).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'medium' });

  const columns: DataTableColumn<ClientErrorItem>[] = [
    { id: 'date', header: t('admin:clientErrors.columns.date'), cell: (r) => <span className="whitespace-nowrap tabular-nums">{fmt(r.created_at)}</span> },
    { id: 'kind', header: t('admin:clientErrors.columns.kind'), cell: (r) => <Badge variant="outline">{t(`admin:clientErrors.kinds.${r.kind}`)}</Badge> },
    {
      id: 'error', header: t('admin:clientErrors.columns.error'),
      cell: (r) => (
        <div className="min-w-[16rem] max-w-[36rem] space-y-1">
          <p className="break-words"><span className="font-semibold">{r.name}</span> {r.message}</p>
          <details>
            <summary className="inline-flex min-h-11 cursor-pointer items-center text-xs text-[var(--vf-accent)] hover:underline">{t('admin:clientErrors.details')}</summary>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-[var(--vf-surface-2)] p-2 font-mono text-[11px]">{r.stack || t('admin:clientErrors.noStack')}</pre>
            <button type="button" className="mt-1 min-h-11 text-xs text-[var(--vf-accent)] hover:underline" onClick={() => setParam('fingerprint', r.fingerprint)}>
              {t('admin:clientErrors.sameError')}
            </button>
          </details>
        </div>
      ),
    },
    { id: 'route', header: t('admin:clientErrors.columns.route'), cell: (r) => <span className="font-mono text-xs">{r.route || '—'}</span> },
    { id: 'browser', header: t('admin:clientErrors.columns.browser'), hideable: true, cell: (r) => r.browser || '—' },
    { id: 'release', header: t('admin:clientErrors.columns.release'), hideable: true, defaultHidden: true, cell: (r) => <span className="font-mono text-xs">{r.release || '—'}</span> },
    { id: 'count', header: t('admin:clientErrors.columns.count'), align: 'right', cell: (r) => <span className="tabular-nums">{r.count}</span> },
  ];

  const pills: FilterPill[] = [
    ...(kind ? [{ id: 'kind', label: t(`admin:clientErrors.kinds.${kind}`), onRemove: () => setParam('kind', '') }] : []),
    ...(route ? [{ id: 'route', label: route, onRemove: () => { setRouteDraft(''); setParam('route', ''); } }] : []),
    ...(q ? [{ id: 'q', label: `« ${q} »`, onRemove: () => { setQDraft(''); setParam('q', ''); } }] : []),
    ...(fingerprint ? [{ id: 'fingerprint', label: t('admin:clientErrors.sameErrorPill'), onRemove: () => setParam('fingerprint', '') }] : []),
  ];
  const clearFilters = () => { setQDraft(''); setRouteDraft(''); setParams(new URLSearchParams(period === '7d' ? {} : { period }), { replace: true }); };

  const err = query.error;
  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6">
      <PlatformPageHeader title={t('admin:superAdminLayout.nav.clientErrors')} subtitle={t('admin:clientErrors.subtitle')} />

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" role="group" aria-label={t('admin:clientErrors.filters')}>
        <Select value={kind || ALL} onValueChange={(v) => setParam('kind', v === ALL ? '' : v)}>
          <SelectTrigger aria-label={t('admin:clientErrors.columns.kind')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('admin:clientErrors.allKinds')}</SelectItem>
            {CLIENT_ERROR_KINDS.map((k) => <SelectItem key={k} value={k}>{t(`admin:clientErrors.kinds.${k}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="search" value={routeDraft} onChange={(e) => setRouteDraft(e.target.value)} placeholder={t('admin:clientErrors.routePlaceholder')} aria-label={t('admin:clientErrors.columns.route')} />
        <Input type="search" value={qDraft} onChange={(e) => setQDraft(e.target.value)} placeholder={t('admin:clientErrors.searchPlaceholder')} aria-label={t('admin:clientErrors.search')} />
        <Select value={period} onValueChange={(v) => { setNow(Date.now()); setParam('period', v === '7d' ? '' : v); }}>
          <SelectTrigger aria-label={t('admin:clientErrors.period.label')}><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => <SelectItem key={p} value={p}>{t(`admin:clientErrors.period.${p}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <DataTable<ClientErrorItem>
        caption={t('admin:superAdminLayout.nav.clientErrors')}
        storageKey="admin-client-errors"
        columns={columns}
        rows={query.data?.items ?? []}
        getRowId={(r) => r.id}
        status={query.isLoading ? 'loading' : query.isError && !query.data ? 'error' : 'ready'}
        error={err ? { message: toUserMessage(err), code: err instanceof ApiErrorException ? err.code : undefined, onRetry: () => void query.refetch() } : undefined}
        filters={pills}
        onClearFilters={clearFilters}
        empty={{ title: t('admin:clientErrors.empty') }}
        pagination={{ page, pageSize: PAGE_SIZE, total: query.data?.total ?? null, onPageChange }}
      />
      <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:clientErrors.retention')}</p>
    </div>
  );
}
